import classcat from "classcat";
import React, { CSSProperties, useMemo } from "react";
import { ToastOptions, toast } from "react-hot-toast";

import { IconTick, IconX } from "@touchtech/web-baselayer-ui";

import css from "./Toaster.module.css";

type Props = {
  title?: string;
  message: string;
  className?: string;
  type: ToastType;
  handleClose: () => void;
};

export enum ToastType {
  LongError = "longError",
  Error = "error",
  Success = "success",
}

const defaultOptions: ToastOptions = {
  position: "top-center",
  duration: 3000,
  className: css.toastContainer,
};

const defaultStyle: CSSProperties = {
  padding: 0,
  margin: 0,
  maxWidth: "100%",
  background: "none",
};

export const ToastVariants: Record<ToastType, { options: ToastOptions }> = {
  [ToastType.LongError]: {
    options: {
      ...defaultOptions,
      duration: 15000,
      style: defaultStyle,
    },
  },
  [ToastType.Error]: {
    options: {
      ...defaultOptions,
      style: defaultStyle,
    },
  },
  [ToastType.Success]: {
    options: {
      ...defaultOptions,
      style: defaultStyle,
    },
  },
};

function Toaster({ title, message, className, type, handleClose }: Props) {
  const Icon = useMemo(
    () => ({
      [ToastType.Success]: (
        <div className={classcat([css.iconContainer, css[type]])}>
          <IconTick className={classcat([css.icon, "text-label-base"])} />
        </div>
      ),
      [ToastType.Error]: (
        <div className={classcat([css.iconContainer, css[type]])}>
          <IconX className={classcat([css.icon, "text-label-base"])} />
        </div>
      ),
      [ToastType.LongError]: null,
    }),
    [type]
  );

  if (type === ToastType.LongError) {
    return (
      <div className={classcat([css.toast, className, css[type]])}>
        <div className="text-label-lg">
          <strong>{title}</strong>
        </div>
        <div className={classcat(["text-label-lg", css.messageContainer])}>
          {message}
        </div>
        <button className={css.closeButton} onClick={handleClose}>
          <IconX />
        </button>
      </div>
    );
  }

  return (
    <div className={classcat([css.toast, className, css[type]])}>
      <div className={classcat([css.withIcon, "text-label-base"])}>
        {Icon[type]}

        <div className="text-label-lg">{message}</div>
      </div>

      <button className={css.closeButton} onClick={handleClose}>
        <IconX />
      </button>
    </div>
  );
}

type ShowToastProps = {
  title?: string;
  message: string;
  options?: ToastOptions;
  type: ToastType;
};

export const showToast = ({
  message,
  title,
  options = {},
  type,
}: ShowToastProps) => {
  toast(
    (toastItem) =>
      Toaster({
        message,
        title,
        type,
        handleClose: () => toast.dismiss(toastItem.id),
      }),
    { ...ToastVariants[type].options, ...options }
  );
};

export default Toaster;
